import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Shield,
  LogOut,
  ArrowUpRight,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { fetchStats } from "@/lib/adminApi";
import { Toaster } from "@/components/ui/sonner";

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);

  const loadStats = () =>
    fetchStats()
      .then(setStats)
      .catch(() => setStats(null));

  useEffect(() => {
    loadStats();
    const i = setInterval(loadStats, 60_000);
    return () => clearInterval(i);
  }, []);

  const items = [
    { to: "/admin", end: true, label: "Overview", icon: LayoutDashboard, count: null },
    {
      to: "/admin/applications",
      label: "Applications",
      icon: Users,
      count: stats?.applications?.unread ?? 0,
    },
    {
      to: "/admin/contacts",
      label: "Contacts",
      icon: MessageSquare,
      count: stats?.contacts?.unread ?? 0,
    },
    {
      to: "/admin/users",
      label: "Admins",
      icon: Shield,
      count: null,
    },
  ];

  const handleLogout = () => {
    logout();
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-bone text-ink flex" data-testid="admin-shell">
      {/* Sidebar */}
      <aside className="w-64 bg-ink text-white flex-shrink-0 flex flex-col sticky top-0 h-screen">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-serif text-[20px]">ThinkRight</span>
            <span className="text-vermilion text-[20px] font-serif">.</span>
            <span className="overline text-white/60 ml-1 text-[10px]">ATS</span>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1" data-testid="admin-nav">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`admin-nav-${it.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-white/65 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1">{it.label}</span>
                {typeof it.count === "number" && it.count > 0 && (
                  <span
                    data-testid={`admin-badge-${it.label.toLowerCase()}`}
                    className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 bg-vermilion text-white text-[11px] font-mono"
                  >
                    {it.count}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-2">
          <div className="px-4 py-2 text-xs text-white/50">
            <div className="overline text-white/40 mb-1">Signed in</div>
            <div className="truncate">{admin?.email}</div>
          </div>
          <button
            data-testid="admin-logout"
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-2 text-xs text-white/40 hover:text-white/80"
          >
            View public site <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0">
        <Outlet context={{ stats, reloadStats: loadStats }} />
      </main>
      <Toaster position="bottom-right" />
    </div>
  );
}
